import styled from "styled-components";
import { useState, FC, MouseEvent, useRef } from "react";
import { Control, useWatch, UseFormSetValue } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";

import { Input } from "@/components/Forms";
import Text from "@/components/Text";
import {
  ChangelogChannelResourse,
  MinimalChannelResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import { useParams } from "react-router-dom";
import { Tags } from "@/pages/AssignTopics/components/AttachmentsField";
import Tag from "@/components/Tag";
import useDebounce from "@/hooks/useDebounce";
import { FormDataT } from "./StrategyFormModal";
import { splitMatchedText } from "@/utils/helpers";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { scrollBarStyles } from "@/utils/theme";

const Wrapper = styled.div`
  position: relative;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
  ${scrollBarStyles};
`;

const AutocompleteButton = styled(Text).attrs({
  $type: "captionSemibold",
  $color: "gray",
})`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.black};
  }
  > span {
    color: ${({ theme }) => theme.colors.purple};
  }
`;

const NoResultsFound = styled(Text)`
  text-align: center;
  padding: 10px 0;
`;

interface Props {
  $hasError?: boolean;
  control: Control<FormDataT>;
  setValue: UseFormSetValue<FormDataT>;
}

const StrategyChannelsField: FC<Props> = ({ $hasError, control, setValue }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setShowDropdown(false));

  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 150);

  const { teamId } = useParams();

  const selectedChannels = useWatch({ control, name: "channels" });

  const { data: channels } = useQuery<MinimalChannelResource[]>(
    ["channels", { teamId, competitors: 0, minimal: true }],
    () =>
      api.get<never, MinimalChannelResource[]>(
        `/v1/teams/${teamId}/channels/list`,
        {
          params: {
            competitors: 0,
          },
        }
      )
  );

  const onChannelAdd = (
    e: MouseEvent<HTMLButtonElement>,
    channelToAdd: ChangelogChannelResourse
  ) => {
    e.stopPropagation();
    setValue("channels", [...selectedChannels, channelToAdd]);
    setShowDropdown(false);
    setSearch("");
  };

  const onChannelRemove = (
    e: MouseEvent<HTMLButtonElement>,
    channelToRemove: ChangelogChannelResourse
  ) => {
    e.stopPropagation();
    setValue(
      "channels",
      selectedChannels.filter(({ id }) => id !== channelToRemove.id)
    );
  };

  const selectedChannelsIds = selectedChannels.map((c) => c.id);

  const filteredChannels = channels?.filter(
    (c) =>
      c.name.toLowerCase().includes(debouncedSearch.toLowerCase()) &&
      !selectedChannelsIds.includes(c.id)
  );

  return (
    <>
      <Wrapper>
        <Input
          placeholder="Add channel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onClick={(e) => e.stopPropagation()}
          $hasError={$hasError}
        />
        {showDropdown && !!channels?.length && (
          <Autocomplete ref={ref}>
            {!!filteredChannels?.length ? (
              filteredChannels.map((c) => (
                <AutocompleteButton
                  key={c.id}
                  type="button"
                  as="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    onChannelAdd(e, c);
                  }}
                  dangerouslySetInnerHTML={{
                    __html: splitMatchedText(c.name, debouncedSearch),
                  }}
                />
              ))
            ) : (
              <NoResultsFound $type="smallMedium" as="div">
                No Results Found
              </NoResultsFound>
            )}
          </Autocomplete>
        )}
      </Wrapper>
      {!!selectedChannels.length && (
        <Tags>
          {selectedChannels.map((c) => {
            return (
              <Tag key={c.id} onDelete={(e) => onChannelRemove(e, c)}>
                {c.name}
              </Tag>
            );
          })}
        </Tags>
      )}
    </>
  );
};

export default StrategyChannelsField;
